/**
 * Custom Attribute API
 * CRUD for user-defined lead/client attributes
 */

import { apiClient } from './aiCrmApi';
import type { ApiResponse } from './aiCrmApi';

export interface CustomAttributeOption {
  id: string;
  attributeId: string;
  label: string;
  value: string;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface CustomAttribute {
  id: string;
  companyId: string;
  name: string;
  appliesTo: 'LEAD' | 'CLIENT' | 'BOTH';
  isRequired: boolean;
  sortOrder: number;
  isActive: boolean;
  options: CustomAttributeOption[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomAttributeInput {
  name: string;
  appliesTo?: 'LEAD' | 'CLIENT' | 'BOTH';
  isRequired?: boolean;
  options: Array<{ label: string; value?: string; color?: string }>;
}

export interface UpdateCustomAttributeInput {
  name?: string;
  appliesTo?: 'LEAD' | 'CLIENT' | 'BOTH';
  isRequired?: boolean;
  isActive?: boolean;
  options?: Array<{ label: string; value?: string; color?: string }>;
}

export const customAttributeApi = {
  getAll: async (appliesTo?: string): Promise<CustomAttribute[]> => {
    const params = appliesTo ? { appliesTo } : {};
    const response = await apiClient.get<ApiResponse<CustomAttribute[]>>(
      '/custom-attributes',
      { params }
    );
    return response.data.data;
  },

  create: async (data: CreateCustomAttributeInput): Promise<CustomAttribute> => {
    const response = await apiClient.post<ApiResponse<CustomAttribute>>(
      '/custom-attributes',
      data
    );
    return response.data.data;
  },

  update: async (id: string, data: UpdateCustomAttributeInput): Promise<CustomAttribute> => {
    const response = await apiClient.put<ApiResponse<CustomAttribute>>(
      `/custom-attributes/${id}`,
      data
    );
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/custom-attributes/${id}`);
  },

  reorder: async (order: Array<{ id: string; sortOrder: number }>): Promise<void> => {
    await apiClient.put('/custom-attributes/reorder', { order });
  },
};
